/**
 * PaperItemPreviewRow — 시험지에 담긴 문제를 읽는 정본 행.
 *
 * 시험지 편집기와 학생 허브의 시험지 선택창이 이 컴포넌트를 함께 쓴다. 두 화면은
 * 데이터를 가져오는 주체만 다르고, 순번·PID·난이도·Math Concept·본문의 정보 구조와
 * 시각 규격은 여기 한 곳에서 결정한다.
 *
 * 학습 오답노트(HubWrongNoteView)는 같은 행에 고르기를 얹는다 — 앞자리(leading: 체크 칸)·뒷자리(footer: 오답 줄)·
 * 고른 표시(selected)·행 누름(onRowClick). 넷 다 주지 않으면 종전 그대로라 두 화면의 행은 바뀌지 않는다.
 */
import React, { memo } from 'react';
import PropTypes from 'prop-types';
import ProblemIdLink from '../ProblemHubV2/ProblemIdLink';
import { difficultyBadgeClass } from '../ProblemHubV2/problemApi';
import { ConceptBadge } from './conceptBadge';
import { LabelBadges } from './labelBadge';
import ProblemBodyPreview from './ProblemBodyPreview';

/** 지면 한 단(423px)과 행 크롬을 합친 편집기 문제 행의 기준 폭. */
export const PAPER_ITEM_ROW_MAX_PX = 485;

const PaperItemPreviewRow = memo(function PaperItemPreviewRow({
  problemId,
  idx,
  concepts = [],
  difficulty = null,
  preview = '',
  labels = '',
  isDeleted = false,
  showBody = true,
  bodyHtml = null,
  bodySeen = false,
  bodyPending = false,
  bodyFallback = null,
  onConceptClick = null,
  selected = false,
  leading = null,
  footer = null,
  onRowClick = null,
}) {
  // 라벨은 본문 대체 문장이 아니라 독립된 탐색 단서다. 본문이 정상적으로 있어도 항상
  // Math Concept 바로 아래에 보여 주며, 본문 폴백에는 문제 요약만 사용해 같은 라벨을 두 번 그리지 않는다.
  const summary = preview;
  const fallback = bodyFallback || summary || null;

  return (
    <li
      data-body-pid={showBody && problemId != null ? String(problemId) : undefined}
      // 행 누름은 보조 입력이다 — 키보드 길은 앞자리의 체크 칸이 맡는다(오답노트)
      onClick={onRowClick ?? undefined}
      className={`flex items-start gap-2 rounded-xl border p-2 ${
        isDeleted ? 'border-amber-200 bg-amber-50' : (selected ? 'border-primary bg-primary/5' : 'border-gray-200')
      } ${onRowClick ? 'cursor-pointer' : ''}`}
    >
      {leading}
      <span className="min-w-9 shrink-0 text-center text-2xl font-bold leading-8 text-primary">
        {String(idx + 1).padStart(2, '0')}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          <ProblemIdLink id={problemId} className="shrink-0" />
          {difficulty != null && (
            <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] ${difficultyBadgeClass(difficulty)}`}>
              난이도 {difficulty}
            </span>
          )}
          {isDeleted && (
            <span
              className="shrink-0 rounded-full border border-amber-200 bg-white px-1.5 py-0.5 text-[9px] font-medium text-amber-800"
              title="원본 문제가 삭제됐습니다 — 이 문항이 남아 있으면 배포·복사·되돌리기가 막힙니다"
            >
              삭제된 문항 — 빼주세요
            </span>
          )}
          {concepts.map((concept, conceptIndex) => (
            <ConceptBadge
              key={`${concept.id ?? 'x'}-${concept.name}-${conceptIndex}`}
              name={concept.name}
              order={concept.order}
              type={concept.type}
              compact
              onClick={concept.id != null && onConceptClick
                ? () => onConceptClick(concept.id)
                : null}
            />
          ))}
        </div>

        <LabelBadges labels={labels} className="mt-0.5 flex flex-wrap items-center gap-1" />

        {showBody ? (
          bodyPending ? (
            <div aria-hidden="true" className="mt-1 overflow-hidden rounded-2xl border border-gray-300 bg-white p-2.5">
              <div className="h-16 animate-pulse">
                <span className="block h-2 w-11/12 rounded-full bg-gray-100" />
                <span className="mt-2 block h-2 w-4/5 rounded-full bg-gray-100" />
                <span className="mt-2 block h-2 w-2/3 rounded-full bg-gray-100" />
              </div>
            </div>
          ) : bodySeen && (bodyHtml || fallback) ? (
            <ProblemBodyPreview html={bodyHtml} fallback={fallback} />
          ) : (
            <div className="mt-1 h-16" aria-hidden="true" />
          )
        ) : (
          summary && <p className="mt-0.5 truncate text-xs text-gray-600">{summary}</p>
        )}
        {footer}
      </div>
    </li>
  );
});

PaperItemPreviewRow.propTypes = {
  problemId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  idx: PropTypes.number.isRequired,
  concepts: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string.isRequired,
    order: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    type: PropTypes.string,
  })),
  difficulty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  preview: PropTypes.string,
  labels: PropTypes.string,
  isDeleted: PropTypes.bool,
  showBody: PropTypes.bool,
  bodyHtml: PropTypes.string,
  bodySeen: PropTypes.bool,
  bodyPending: PropTypes.bool,
  bodyFallback: PropTypes.node,
  onConceptClick: PropTypes.func,
  selected: PropTypes.bool,
  leading: PropTypes.node,
  footer: PropTypes.node,
  onRowClick: PropTypes.func,
};

export default PaperItemPreviewRow;
